import { useCurrentUser } from 'components/providers'
import {
  DocumentChangeType,
  FirestoreError,
  QueryConstraint,
  QueryDocumentSnapshot,
  addDoc,
  doc,
  collection as firestoreCollection,
  deleteDoc as firestoreDeleteDoc,
  onSnapshot as firestoreOnSnapshot,
  setDoc as firestoreSetDoc,
  updateDoc as firestoreUpdateDoc,
  getCountFromServer,
  getDoc,
  getFirestore,
  query,
  writeBatch,
} from 'firebase/firestore'
import { isEqual } from 'lodash-es'
import { useEffect, useRef, useState } from 'react'
import { Collection, CollectionName, DBProps, OmitDBProps } from 'types'
import firebaseApp from './firebase'
import { removeEmpty } from './helpers'

export const db = getFirestore(firebaseApp)

export async function createDoc<T extends CollectionName>(
  userId: string,
  collection: T,
  data: OmitDBProps<Collection[T]>
) {
  const created = createTimestamp()
  const createdBy = userId
  const updated = created
  const updatedBy = createdBy

  //@ts-ignore
  const { id, ...noId } = data // remove accidental id
  const body = removeEmpty({ ...noId, created, createdBy, updated, updatedBy })
  const docRef = await addDoc(firestoreCollection(db, collection), body)
  return { id: docRef.id, ...body } as Collection[T]
}

export async function getDocById<T extends CollectionName>(collection: T, id: string) {
  const docSnap = await getDoc(doc(db, collection, id))
  if (!docSnap.exists()) return null

  return { id: docSnap.id, ...docSnap.data() } as Collection[T]
}

export async function updateDoc<T extends CollectionName>(
  userId: string,
  collection: T,
  body: { id: string } & Partial<Collection[T]>
) {
  const { id, created, createdBy, ...rest } = body
  const ref = doc(db, collection, id)
  const updated = createTimestamp()
  const updatedBy = userId
  const data = removeEmpty({ ...rest, updated, updatedBy })
  await firestoreUpdateDoc(ref, data)
  return { id, ...data } as Collection[T]
}

export async function setDoc<T extends CollectionName>(
  userId: string,
  collection: T,
  body: OmitDBProps<Collection[T]> & Partial<DBProps> & { id: string }
) {
  const { id, created = createTimestamp(), createdBy = userId, ...rest } = body
  const ref = doc(db, collection, id)
  const updated = createTimestamp()
  const updatedBy = userId
  const data = removeEmpty({ ...rest, created, createdBy, updated, updatedBy })
  await firestoreSetDoc(ref, data)
  return { id, ...data } as Collection[T]
}

export function deleteDoc<T extends CollectionName>(collection: T, id: string) {
  return firestoreDeleteDoc(doc(db, collection, id))
}

export async function getCount<T extends CollectionName>(
  collection: T,
  ...constraints: QueryConstraint[]
) {
  const ref = query(firestoreCollection(db, collection), ...constraints)
  const snapshot = await getCountFromServer(ref)
  return snapshot.data().count
}

export type FetchStatus = 'loading' | 'error' | 'success' | 'empty'

export function useDoc<T extends CollectionName>(collection: T, id?: string) {
  const user = useCurrentUser()
  const [data, setData] = useState<Collection[T] | null | undefined>(undefined)
  const [status, setStatus] = useState<FetchStatus>('loading')

  useEffect(() => {
    setStatus('loading')
    if (!user || !id) {
      setData(user ? undefined : null)
      setStatus(user === null ? 'error' : user ? 'empty' : 'loading')
      return
    }

    const unsubscribe = onDoc(collection, id, ({ doc, error }) => {
      setStatus(error ? 'error' : 'success')
      setData(doc)
    })

    return unsubscribe
  }, [collection, user, id])

  return [data, status] as const
}

export interface OnDocCallbackProps<T extends CollectionName> {
  doc: Collection[T] | null
  error?: FirestoreError
}

export function onDoc<T extends CollectionName>(
  collection: T,
  id: string,
  callback: (props: OnDocCallbackProps<T>) => void
) {
  const documentRef = doc(db, collection, id)

  return firestoreOnSnapshot(
    documentRef,
    snap => {
      callback({ doc: snap.exists() ? makeDoc<Collection[T]>(snap) : null })
    },
    error => {
      callback({ doc: null, error })
    }
  )
}

export function useDocs<T extends CollectionName>(collection: T, constraints?: QueryConstraint[]) {
  const user = useCurrentUser()
  const [data, setData] = useState<Collection[T][]>([])
  const [status, setStatus] = useState<FetchStatus>('loading')

  const constraintsRef = useRef(constraints)
  if (!isEqual(constraintsRef.current, constraints)) constraintsRef.current = constraints

  useEffect(() => {
    setStatus('loading')
    if (!user || !constraintsRef.current) return

    const unsubscribe = onDocs(collection, constraintsRef.current, ({ docs, error }) => {
      setData(docs)
      setStatus(error ? 'error' : 'success')
    })

    return unsubscribe
  }, [collection, user, constraintsRef.current])

  return [data, status] as [Collection[T][], FetchStatus]
}

export interface OnDocsCallbackProps<T extends CollectionName> {
  docs: Collection[T][]
  changes: DocumentChange<T>[]
  error?: FirestoreError
}

interface DocumentChange<N extends CollectionName> {
  type: DocumentChangeType
  data: Collection[N]
}

export function onDocs<T extends CollectionName>(
  collection: T,
  constraints: QueryConstraint[],
  callback: (props: OnDocsCallbackProps<T>) => void
) {
  const q = query(firestoreCollection(db, collection), ...constraints)
  return firestoreOnSnapshot(
    q,
    snap => {
      const docs = snap.docs.map(doc => makeDoc<Collection[T]>(doc))
      const changes = snap.docChanges().map(({ type, doc }) => {
        return { type, data: makeDoc<Collection[T]>(doc) }
      })
      callback({ docs, changes })
    },
    error => {
      callback({ docs: [], changes: [], error })
    }
  )
}

export function makeDoc<T>(doc: QueryDocumentSnapshot): T {
  return { ...doc.data(), id: doc.id } as T
}

export function splitDBProps<T extends DBProps>(doc?: T | null) {
  if (!doc) return [{} as Partial<DBProps>, {} as Partial<OmitDBProps<T>>] as const

  const { id, created, createdBy, updated, updatedBy, ...rest } = doc
  const dbProps: DBProps = { id, created, createdBy, updated, updatedBy }
  return [dbProps, rest] as const
}

export type BatchRow<T extends CollectionName> =
  | BatchCreateRow<T>
  | BatchUpdateRow<T>
  | BatchDeleteRow

export interface BatchCreateRow<T extends CollectionName> {
  type: 'create'
  body: Partial<Collection[T]>
}

export interface BatchUpdateRow<T extends CollectionName> {
  type: 'update'
  body: Partial<Collection[T]> & { id: string }
}

export interface BatchDeleteRow {
  type: 'delete'
  body: { id: string }
}

export async function batchWrite<T extends CollectionName>(
  userId: string,
  collection: T,
  data: BatchRow<T>[],
  onProgress?: (progress: number) => void,
  abortSignal?: AbortSignal,
  cleanup?: () => void,
  batchSize = 50
) {
  const batchedData = data.reduce((acc, doc, i) => {
    if (i % batchSize === 0) acc.push([])
    acc[acc.length - 1].push(doc)
    return acc
  }, [] as BatchRow<T>[][])
  let filesWritten = 0
  try {
    for (let chunk of batchedData) {
      if (abortSignal?.aborted) {
        if (cleanup) {
          cleanup()
          throw new Error('Batch write aborted, running cleanup')
        } else throw new Error('Batch write aborted')
      }
      const batch = writeBatch(db)
      for (let row of chunk) {
        if (row.type === 'create') {
          const { created = createTimestamp(), createdBy = userId, ...rest } = row.body
          const updated = created,
            updatedBy = userId
          batch.set(doc(db, collection, generateId()), {
            created,
            createdBy,
            updated,
            updatedBy,
            ...rest,
          })
        } else if (row.type === 'update') {
          const { id, ...rest } = row.body
          const updated = createTimestamp(),
            updatedBy = userId
          batch.set(doc(db, collection, id), { ...rest, updated, updatedBy }, { merge: true })
        } else if (row.type === 'delete') {
          const { id } = row.body
          batch.delete(doc(db, collection, id))
        }
      }
      await batch.commit()
      filesWritten += chunk.length
      onProgress?.((filesWritten / data.length) * 100)
    }
  } catch (err) {
    cleanup?.()
    throw err
  }
}

/** Generate random id in same shape as firestore */
export function generateId() {
  const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'

  let autoId = ''
  for (let i = 0; i < 20; i++) {
    autoId += CHARS.charAt(Math.floor(Math.random() * CHARS.length))
  }

  return autoId
}

export const createTimestamp = () => new Date().getTime()
